import React, {Component} from "react"

import {FormattedMessage, InjectIntl, injectIntl} from "@irati/utils/lang"
import {Row, Col} from "react-bootstrap"
import {H2, H4} from "@irati/ui/src/Typography"
import {Label3} from "@irati/ui/src/Labels"
import Form from "@irati/ui/src/Form"
import ValidationButton from "@irati/ui/src/FormItem/ValidationButton"
import Validation from "@irati/utils/validation"
import Header from "@irati-web/fields/companyBenefits/Header"
import SavingsValue from "@irati-web/fields/companyBenefits/SavingsValue"
import SavingsType, {CURRENCY, PROPERTY} from "@irati-web/fields/companyBenefits/SavingsType"
import CardImage from "@irati-web/fields/companyBenefits/CardImage"
import {Hidden} from "@irati/ui/src/FormItem"
import Benefit from "@irati/deals/types/Benefit"
import TeamSelect from "@irati-web/fields/companyBenefits/TeamSelect"
import {officesForTenant} from "@irati/identity/utils/tenant/hasMultipleOffices"
import {Office} from "@irati-web/fields/user"
import adminTracking from "@irati-tracking/admin"

import Flex from "@irati/ui/src/Flex"
import Div from "./styled/Div"
import ErrorWrapper from "./styled/ErrorWrapper"
import {DESCRIPTION} from "./Popup"
import WhoCanSee, {WhoCanSeeCompanyBenefit, WhoCanSeeOnChange} from "./fields/WhoCanSee"

interface State {
  whoCanSee: WhoCanSeeCompanyBenefit,
  benefit?: Benefit,
  hasBusinessUnits: boolean
}

interface Props extends InjectIntl {
  show: boolean,
  benefit: Benefit,
  onChange: (property: string, value: any) => void,
  selectStep: (description: string) => void,
  hideBannerImageUpload?: boolean
}

class CardStep extends Component<Props> {
  componentDidMount() {
    const {benefit} = this.state
    officesForTenant().then((businessUnits) => {
      let whoCanSee = this.state.whoCanSee
      const buIds = businessUnits.map((t) => t.uuid)

      if(benefit?.teamIds?.length && buIds?.length) {
        const areTeamIdsBusinessUnits = buIds.some((test) => benefit.teamIds.indexOf(test) > -1)
        whoCanSee = areTeamIdsBusinessUnits ? WhoCanSeeCompanyBenefit.BusinessUnit : WhoCanSeeCompanyBenefit.Team
      }

      this.setState({
        hasBusinessUnits: !!businessUnits?.length,
        whoCanSee
      })
    })
  }

  static getDerivedStateFromProps(props: Props, state: State) {
    const {benefit} = props

    let {whoCanSee} = state
    if (benefit.uuid !== state.benefit?.uuid) {
      const hasWhitelist = benefit.teamIds.length
      whoCanSee = hasWhitelist ? WhoCanSeeCompanyBenefit.Team : WhoCanSeeCompanyBenefit.Tenant
    }
    return {whoCanSee, benefit}
  }

  state: State = {
    whoCanSee: WhoCanSeeCompanyBenefit.Tenant,
    hasBusinessUnits: false
  }

  schema() {
    return Validation.mixed().test(
      "ok",
      this.props.formatMessage({id: "admin.components.card.savingsSchema.validation"}),
      (benefit: Benefit) => {
        const {value, type} = benefit.savings.userSavings
        return !((!value && type) || (value && !type))
      }
    )
  }

  get edit(): boolean {
    return !!this.props.benefit.cardImage
  }

  handleWhoCanSee: WhoCanSeeOnChange = (whoCanSee) => {
    adminTracking.companyBenefitsSelectAudience()
    const {benefit} = this.props
    this.setState({whoCanSee})
    this.props.onChange("teamIds", [])
    if (whoCanSee === WhoCanSeeCompanyBenefit.BusinessUnit && benefit?.savings?.userSavings?.type === CURRENCY) {
      this.props.onChange(PROPERTY, null)
    }
  }

  handleContinue = () => {
    adminTracking.companyBenefitsCreateTitle()
    this.props.selectStep(DESCRIPTION)
  }

  render() {
    const {show, benefit, onChange, formatMessage, hideBannerImageUpload} = this.props
    const {whoCanSee, hasBusinessUnits} = this.state

    if (!show) {
      return null
    }

    return (
      <Div>
        <Flex direction="column" alignItems="center">
          <Label3 uppercase>
            <FormattedMessage id="admin.components.card.stepOne" />
          </Label3>
          <H2>
            <FormattedMessage id="admin.components.card.headerTwo.companyBenefit" />
          </H2>
          <H2>
            <FormattedMessage id="admin.components.card.headerTwo.card" />
          </H2>
          <H4 margin="40px 0 60px">
            <FormattedMessage id="admin.components.card.headerFour.defineBenefit" />
          </H4>
        </Flex>
        <Row>
          <Col md={12}>
            <Form form="create-company-benefit-step1">
              <Col md={12}>
                <WhoCanSee value={whoCanSee} onChange={this.handleWhoCanSee} showBUOption={hasBusinessUnits} />
              </Col>
              {whoCanSee === WhoCanSeeCompanyBenefit.Team && (
                <Col md={12}>
                  <TeamSelect value={benefit.teamIds} onChange={(values) => onChange("teamIds", values.map(({value}) => value))} />
                </Col>
              )}
              {whoCanSee === WhoCanSeeCompanyBenefit.BusinessUnit && (
                <Col md={12}>
                  <Office isMulti value={benefit.teamIds} onChange={(values) => onChange("teamIds", values.map(({value}) => value))} />
                </Col>
              )}
              <Col md={12}>
                <Header benefit={benefit} onChange={onChange} />
              </Col>
              <Col md={7}>
                <SavingsValue benefit={benefit} onChange={onChange} />
              </Col>
              <Col md={5}>
                <SavingsType
                  benefit={benefit}
                  onChange={onChange}
                  percentageOnly={whoCanSee === WhoCanSeeCompanyBenefit.BusinessUnit} />
              </Col>
              <Col md={12}>
                <ErrorWrapper>
                  <Hidden name="savings" value={benefit} validation={this.schema()} />
                </ErrorWrapper>
              </Col>
              <Col md={12}>
                <CardImage
                  benefit={benefit}
                  edit={this.edit}
                  onChange={onChange}
                  heading="admin.components.cardImage.heading.benefitImage"
                  name="benefit_card_image"
                  property="cardImage"
                  width={360}
                  height={160} />
              </Col>
              {!hideBannerImageUpload && (
                <Col md={12}>
                  <CardImage
                    benefit={benefit}
                    edit={this.edit}
                    onChange={onChange}
                    validation={() => Validation.string()}
                    heading="admin.components.cardImage.heading.benefitBanner"
                    name="benefit_banner_image"
                    property="bannerImage"
                    width={1920}
                    height={350} />
                </Col>
              )}
            </Form>
          </Col>
          <Col xs={12}>
            <Flex justifyContent="center">
              <ValidationButton
                primary
                large
                loading={formatMessage({id: "admin.components.card.validationButton.loading"})}
                onClick={this.handleContinue}>
                <FormattedMessage id="admin.components.card.validationButton.continue" />
              </ValidationButton>
            </Flex>
          </Col>
        </Row>
      </Div>
    )
  }
}

export default injectIntl(CardStep)
